import { ChangeEventHandler, FC, MutableRefObject, useContext } from 'react';

import { Flex, FlexProps, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { Deal, DealStateEnum } from '../../../gql/dealGql';

import NumberInput from '../../shared/NumberInput';

import InputRow from './InputRow';
import VscRatesModal from './VscRatesModal';
import CalcGapButton from './buttons/CalcGapButton';
import CalcKeyReplacementButton from './buttons/CalcKeyReplacementButton';
import CalcTireWheelButton from './buttons/CalcTireWheelButton';

import { PermissionEnum } from '../../../constants/permissions';
import { useProductAutoSave } from '../../../hooks/useProductAutoSave';
import { Can } from '../../../libs/Can';
import { DealContext } from '../../../libs/DealContext';
import { AbilityContext } from '../../../libs/contextLib';
import { formatMoney } from '../../../libs/utils';

type ProductsSectionProps = FlexProps & {
  isEditable: boolean;
  inputIsReadOnlyByPermissions: boolean;
  customHandleChange?: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
  // Re-persists the currently selected package to deal_products (published by
  // PaymentOptions). Called when a product rate is calculated/selected so the
  // new values are actually written, not just held in Formik.
  persistSelectedPackageRef?: MutableRefObject<(() => Promise<void>) | null>;
};

export const ProductsSection: FC<ProductsSectionProps> = ({
  isEditable,
  inputIsReadOnlyByPermissions,
  customHandleChange,
  persistSelectedPackageRef,
  ...rest
}) => {
  const { deal } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  const { values, handleChange: formikHandleChange } = useFormikContext<Deal>();
  const handleChange = customHandleChange ?? formikHandleChange;

  const { refetch, debouncedSave, isSavingProductPrices } = useProductAutoSave();

  // Wraps the regular handleChange to also trigger deal_products auto-save.
  // This fires on a VSC grid pick too (the controlled NumberInput re-emits the
  // programmatic setFieldValue): that auto-save is what writes the picked
  // price/cost into deal_products, so the deal_products→Formik sync-back
  // doesn't revert them to the stale values before Save reads Formik.
  const productHandleChange: ChangeEventHandler<HTMLInputElement | HTMLSelectElement> = (e) => {
    handleChange(e);
    debouncedSave();
  };

  const inClosing = deal.state === DealStateEnum.Closing;
  const inStructuring = deal.state === DealStateEnum.Structuring;
  const showProducts = inClosing || inStructuring;

  // Persist the just-calculated/selected product to deal_products by re-saving
  // the current package, THEN refetch. Without the persist, a rate selected in
  // the VSC grid (or a Calc button) only lives in Formik and is lost on the
  // next form re-derivation from the unchanged deal_products.
  const onRatesLoaded = async () => {
    await persistSelectedPackageRef?.current?.();
    await refetch();
  };

  return (
    <Flex direction="column" {...rest}>
      <InputRow label="VSC Terms">
        <Text variant="dealInfoBuyout">
          {values.financial_info?.vsc_type ? `${values.financial_info?.vsc_type} - ` : ''}
          {values.financial_info?.vsc_term || ''}
        </Text>
      </InputRow>

      <InputRow label="VSC">
        {abilities.has(PermissionEnum.EditPriceAndCost) ? (
          <>
            <NumberInput
              name="financial_info.vsc_price"
              isMoney
              showThousandSeparator
              additionalHandleChange={productHandleChange}
              isDisabled={inputIsReadOnlyByPermissions || !isEditable}
            />
            <Can I={PermissionEnum.SeeCosts}>
              <NumberInput
                name="financial_info.vsc_cost"
                isMoney
                showThousandSeparator
                additionalHandleChange={productHandleChange}
                isDisabled={inputIsReadOnlyByPermissions || !isEditable}
              />
            </Can>
          </>
        ) : (
          <>
            <Text w="50%" variant="dealInfoBuyout">
              {formatMoney(values.financial_info?.vsc_price)}
            </Text>
            <Can I={PermissionEnum.SeeCosts}>
              <Text w="50%" variant="dealInfoBuyout">
                {formatMoney(values.financial_info?.vsc_cost)}
              </Text>
            </Can>
          </>
        )}
      </InputRow>

      <InputRow label="GAP">
        {abilities.has(PermissionEnum.EditPriceAndCost) ? (
          <>
            <NumberInput
              name="financial_info.gap_price"
              isMoney
              showThousandSeparator
              additionalHandleChange={productHandleChange}
              isDisabled={inputIsReadOnlyByPermissions || !isEditable}
            />
            <Can I={PermissionEnum.SeeCosts}>
              <NumberInput
                name="financial_info.gap_cost"
                isMoney
                showThousandSeparator
                additionalHandleChange={productHandleChange}
                isDisabled={inputIsReadOnlyByPermissions || !isEditable}
              />
            </Can>
          </>
        ) : (
          <>
            <Text w="50%" variant="dealInfoBuyout">
              {formatMoney(values.financial_info?.gap_price)}
            </Text>
            <Can I={PermissionEnum.SeeCosts}>
              <Text w="50%" variant="dealInfoBuyout">
                {formatMoney(values.financial_info?.gap_cost)}
              </Text>
            </Can>
          </>
        )}
      </InputRow>

      <InputRow label="Tire & Wheel">
        {abilities.has(PermissionEnum.EditPriceAndCost) ? (
          <>
            <NumberInput
              name="tire_wheel_price"
              isMoney
              showThousandSeparator
              additionalHandleChange={productHandleChange}
              isDisabled={inputIsReadOnlyByPermissions || !isEditable}
            />
            <Can I={PermissionEnum.SeeCosts}>
              <NumberInput
                name="tire_wheel_cost"
                isMoney
                showThousandSeparator
                additionalHandleChange={productHandleChange}
                isDisabled={inputIsReadOnlyByPermissions || !isEditable}
              />
            </Can>
          </>
        ) : (
          <>
            <Text w="50%" variant="dealInfoBuyout">
              {formatMoney(values.tire_wheel_price)}
            </Text>
            <Can I={PermissionEnum.SeeCosts}>
              <Text w="50%" variant="dealInfoBuyout">
                {formatMoney(values.tire_wheel_cost)}
              </Text>
            </Can>
          </>
        )}
      </InputRow>

      <InputRow label="Key Replace">
        {abilities.has(PermissionEnum.EditPriceAndCost) ? (
          <>
            <NumberInput
              name="key_replacement_price"
              isMoney
              showThousandSeparator
              additionalHandleChange={productHandleChange}
              isDisabled={inputIsReadOnlyByPermissions || !isEditable}
            />
            <Can I={PermissionEnum.SeeCosts}>
              <NumberInput
                name="key_replacement_cost"
                isMoney
                showThousandSeparator
                additionalHandleChange={productHandleChange}
                isDisabled={inputIsReadOnlyByPermissions || !isEditable}
              />
            </Can>
          </>
        ) : (
          <>
            <Text w="50%" variant="dealInfoBuyout">
              {formatMoney(values.key_replacement_price)}
            </Text>
            <Can I={PermissionEnum.SeeCosts}>
              <Text w="50%" variant="dealInfoBuyout">
                {formatMoney(values.key_replacement_cost)}
              </Text>
            </Can>
          </>
        )}
      </InputRow>

      <Flex
        justifyContent="start"
        mt={3}
        gap={2}
        direction={{ base: 'row', xl: 'column', '2xl': 'row' }}
        flexWrap="wrap"
      >
        {showProducts && (
          <>
            <VscRatesModal
              onRatesLoaded={onRatesLoaded}
              isSavingProductPrices={isSavingProductPrices}
            />
            <CalcGapButton onRatesLoaded={onRatesLoaded} />
            <CalcTireWheelButton onRatesLoaded={onRatesLoaded} />
            <CalcKeyReplacementButton onRatesLoaded={onRatesLoaded} />
          </>
        )}
      </Flex>
    </Flex>
  );
};
